/**
 * Alta de un perfil PML (T6, 19/10/2026; doc de Sol "Brain para Paid Media", sección
 * "Alta de clientes: desde la UI y desde el MCP"). Lo que el formulario de /brain/clients y
 * el tool `propose_pml_profile` del MCP mandan es la MISMA lista plana de campos
 * (`ProfileProposalFields`); de acá sale el JSON del perfil (`profileConfigFromFields`), que
 * después valida `brainProfileConfigSchema` como cualquier otro. Las dos vías escriben lo
 * mismo; no hay una tercera configuración.
 *
 * Puro (sin I/O): lo usan el front (preview del JSON y parseo de links), el backend
 * (lib/brain-pr-proposals.ts) y los tests.
 */
import { foldKey, slugify } from './text-normalize';
import {
  BRAIN_PROFILE_WEEKDAYS,
  type BrainProfileConfigInput,
  type BrainProfileWeekday,
} from './brain-profile-config';

export interface ProfileProposalMeeting {
  /** Id del evento recurrente de Calendar (o lo que pegó la persona: link o id de instancia; ver `recurringEventIdFromInput`). */
  recurringEventId: string;
  label?: string;
  calendarOf?: string;
}

/** Los campos planos del alta: formulario y tool MCP mandan exactamente esto. */
export interface ProfileProposalFields {
  clientId: string;
  /** Slug de la BU ("rts"); el id del perfil es `<clientId>.<bu>`. */
  bu: string;
  buName: string;
  buEmoji?: string;
  owner: string;
  managerName: string;
  managerEmail?: string;
  alertsChannel?: string;
  channelIds: string[];
  sharedChannelIds: string[];
  buFilter: string[];
  meetings: ProfileProposalMeeting[];
  sidesClient: string[];
  sidesWinclap: string[];
  gmailLabel?: string;
  gmailMailboxes: string[];
  chatSpaces: string[];
  chatReader?: string;
  analyticsKey?: string;
  statusEnabled: boolean;
  statusDays: string[];
  statusOutputChannel?: string;
}

export function emptyProfileProposalFields(over: Partial<ProfileProposalFields> = {}): ProfileProposalFields {
  return {
    clientId: '', bu: '', buName: '', owner: '', managerName: '',
    channelIds: [], sharedChannelIds: [], buFilter: [], meetings: [], sidesClient: [], sidesWinclap: [],
    gmailMailboxes: [], chatSpaces: [], statusEnabled: false, statusDays: [],
    ...over,
  };
}

const clean = (s: string | undefined | null): string => (s ?? '').replace(/\s+/g, ' ').trim();
const cleanList = (xs: readonly string[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const x of xs) {
    const v = clean(x);
    if (v && !seen.has(v.toLowerCase())) { seen.add(v.toLowerCase()); out.push(v); }
  }
  return out;
};
const lowerList = (xs: readonly string[]): string[] => cleanList(xs).map((x) => x.toLowerCase());

/** "a, b\nc" → ["a","b","c"] (coma o salto de línea; sin vacíos ni repetidos). */
export function splitProposalList(raw: string | undefined | null): string[] {
  return cleanList((raw ?? '').split(/[,\n]/));
}

/** Slug de BU (o de cliente) a partir de un nombre ("Rappitenderos (RTs)" → "rappitenderos-rts"); `BRAIN_PROFILE_BU_RE` manda. */
export const buSlugOf = (name: string): string => slugify(name, 30);

const WEEKDAY_ALIASES: Record<string, BrainProfileWeekday> = {
  monday: 'monday', mon: 'monday', lunes: 'monday', lun: 'monday', segunda: 'monday', 'segunda-feira': 'monday',
  tuesday: 'tuesday', tue: 'tuesday', martes: 'tuesday', mar: 'tuesday', terca: 'tuesday', 'terca-feira': 'tuesday',
  wednesday: 'wednesday', wed: 'wednesday', miercoles: 'wednesday', mie: 'wednesday', quarta: 'wednesday', 'quarta-feira': 'wednesday',
  thursday: 'thursday', thu: 'thursday', jueves: 'thursday', jue: 'thursday', quinta: 'thursday', 'quinta-feira': 'thursday',
  friday: 'friday', fri: 'friday', viernes: 'friday', vie: 'friday', sexta: 'friday', 'sexta-feira': 'friday',
};

/** Días en inglés, español o portugués → los slugs de `BRAIN_PROFILE_WEEKDAYS`, en orden de semana. Lo que no se entiende se ignora. */
export function parseProposalWeekdays(raw: readonly string[]): BrainProfileWeekday[] {
  const picked = new Set<BrainProfileWeekday>();
  for (const r of raw) {
    const k = foldKey(r);
    const d = WEEKDAY_ALIASES[k];
    if (d) picked.add(d);
  }
  return BRAIN_PROFILE_WEEKDAYS.filter((d) => picked.has(d));
}

/** Sufijo de instancia de un evento recurrente: `<serie>_20261014T140000Z` (también `_20261014`). */
const INSTANCE_SUFFIX_RE = /_\d{8}(T\d{6}Z?)?$/;

function base64UrlDecode(s: string): string | null {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (s.length % 4)) % 4);
  try {
    const atobFn = (globalThis as { atob?: (x: string) => string }).atob;
    if (atobFn) return atobFn(b64);
    const BufferCtor = (globalThis as { Buffer?: { from(x: string, enc: string): { toString(enc: string): string } } }).Buffer;
    return BufferCtor ? BufferCtor.from(b64, 'base64').toString('utf8') : null;
  } catch {
    return null;
  }
}

/**
 * El id del evento RECURRENTE a partir de lo que pega la persona: el id pelado, el id de una
 * instancia (`<serie>_20261014T140000Z` → `<serie>`), o el link del evento en Google Calendar
 * (`?eid=<base64url de "<eventId> <calendario>">` o `/eventedit/<base64url>`). Devuelve null si
 * no se reconoce nada. No consulta Calendar: la existencia la verifica el backend.
 */
export function recurringEventIdFromInput(raw: string): string | null {
  const s = clean(raw);
  if (!s) return null;
  let id = s;
  if (/^https?:\/\//i.test(s)) {
    let encoded: string | null = null;
    try {
      const u = new URL(s);
      encoded = u.searchParams.get('eid');
      if (!encoded) {
        const m = u.pathname.match(/\/(?:eventedit|event)\/([A-Za-z0-9_-]+)/);
        encoded = m?.[1] ?? null;
      }
    } catch {
      return null;
    }
    if (!encoded) return null;
    const decoded = base64UrlDecode(encoded);
    if (!decoded) return null;
    id = decoded.split(/\s+/)[0] ?? '';
  }
  id = id.replace(INSTANCE_SUFFIX_RE, '');
  return /^[A-Za-z0-9_-]{5,}$/.test(id) ? id : null;
}

/** `C0…`, `#nombre`, `nombre` o el link `…/archives/C0…` de Slack → `{ id }` o `{ name }` para resolver en el backend. */
export function slackChannelRefFromInput(raw: string): { id: string } | { name: string } | null {
  const s = clean(raw);
  if (!s) return null;
  const link = s.match(/\/archives\/([CG][A-Z0-9]{6,})/);
  if (link) return { id: link[1] };
  const bare = s.replace(/^#/, '');
  // Un id de Slack es mayúsculas + dígitos; los nombres de canal son minúsculas: "#C0…" es un id con numeral de más.
  if (/^[CG][A-Z0-9]{6,}$/.test(bare)) return { id: bare };
  const name = bare.toLowerCase();
  return /^[a-z0-9._-]{1,80}$/.test(name) ? { name } : null;
}

/** `spaces/XXXX` o el link `https://chat.google.com/room/XXXX` / `…/space/XXXX` → `spaces/XXXX`; null si no se reconoce. */
export function chatSpaceFromInput(raw: string): string | null {
  const s = clean(raw);
  if (!s) return null;
  const m = s.match(/(?:^spaces\/|chat\.google\.com\/(?:room|space|dm)\/)([A-Za-z0-9_-]+)/);
  return m ? `spaces/${m[1]}` : null;
}

export interface ProfileConfigFromFieldsOptions {
  /** Fecha de revisión que lleva el JSON (quien lo propone acaba de revisarlo). */
  reviewedAt: string;
}

/**
 * El JSON del perfil a partir de los campos planos. Omite lo vacío (gmail sin etiqueta, chat
 * sin spaces, status apagado sin días ni canal) para que el archivo quede como los escritos a
 * mano. NO valida: eso lo hace `parseBrainProfileConfig` sobre el resultado.
 */
export function profileConfigFromFields(f: ProfileProposalFields, opts: ProfileConfigFromFieldsOptions): BrainProfileConfigInput {
  const clientId = clean(f.clientId).toLowerCase();
  const bu = clean(f.bu).toLowerCase();
  const meetings = f.meetings
    .map((m) => ({ recurringEventId: recurringEventIdFromInput(m.recurringEventId) ?? clean(m.recurringEventId), label: clean(m.label) || undefined, calendarOf: clean(m.calendarOf).toLowerCase() || undefined }))
    .filter((m) => m.recurringEventId)
    .map((m) => ({ recurringEventId: m.recurringEventId, ...(m.label ? { label: m.label } : {}), ...(m.calendarOf ? { calendarOf: m.calendarOf } : {}) }));
  const sidesClient = cleanList(f.sidesClient);
  const sidesWinclap = cleanList(f.sidesWinclap);
  const gmailLabel = clean(f.gmailLabel);
  const chatSpaces = cleanList(f.chatSpaces);
  const statusDays = parseProposalWeekdays(f.statusDays);
  const statusOutputChannel = clean(f.statusOutputChannel) || undefined;
  const managerEmail = clean(f.managerEmail).toLowerCase() || undefined;
  const alertsChannel = clean(f.alertsChannel) || undefined;
  const buEmoji = clean(f.buEmoji) || undefined;
  const chatReader = clean(f.chatReader).toLowerCase() || undefined;
  const analyticsKey = clean(f.analyticsKey) || undefined;

  return {
    id: `${clientId}.${bu}`,
    clientId,
    solution: 'pml',
    bu: { name: clean(f.buName), ...(buEmoji ? { emoji: buEmoji } : {}) },
    owner: clean(f.owner).toLowerCase(),
    manager: { name: clean(f.managerName), ...(managerEmail ? { email: managerEmail } : {}), ...(alertsChannel ? { alertsChannel } : {}) },
    meetings,
    slack: { channelIds: cleanList(f.channelIds), sharedChannelIds: cleanList(f.sharedChannelIds), buFilter: cleanList(f.buFilter) },
    ...(sidesClient.length || sidesWinclap.length ? { sides: { client: sidesClient, winclap: sidesWinclap } } : {}),
    ...(gmailLabel ? { gmail: { label: gmailLabel, mailboxes: lowerList(f.gmailMailboxes) } } : {}),
    ...(chatSpaces.length ? { googleChat: { spaces: chatSpaces, ...(chatReader ? { reader: chatReader } : {}) } } : {}),
    ...(analyticsKey ? { analyticsKey } : {}),
    ...(f.statusEnabled || statusDays.length || statusOutputChannel
      ? { status: { enabled: !!f.statusEnabled, days: statusDays, ...(statusOutputChannel ? { outputChannel: statusOutputChannel } : {}) } }
      : {}),
    reviewedAt: opts.reviewedAt,
  };
}

const RRULE_DAYS: Record<string, string> = { MO: 'lun', TU: 'mar', WE: 'mié', TH: 'jue', FR: 'vie', SA: 'sáb', SU: 'dom' };

/** "semanal, mié" / "cada 2 semanas, mar y jue" / "mensual" a partir de las RRULE de un evento de Calendar; "" si no hay. */
export function describeRrule(recurrence: readonly string[] | undefined | null): string {
  const rule = (recurrence ?? []).find((r) => r.toUpperCase().startsWith('RRULE:'));
  if (!rule) return '';
  const parts = Object.fromEntries(rule.slice(6).split(';').map((kv) => { const [k, v] = kv.split('='); return [k.toUpperCase(), (v ?? '').toUpperCase()]; }));
  const interval = Number(parts.INTERVAL ?? '1') || 1;
  const freq = parts.FREQ ?? '';
  const base = freq === 'DAILY' ? (interval > 1 ? `cada ${interval} días` : 'diaria')
    : freq === 'WEEKLY' ? (interval > 1 ? `cada ${interval} semanas` : 'semanal')
    : freq === 'MONTHLY' ? (interval > 1 ? `cada ${interval} meses` : 'mensual')
    : freq === 'YEARLY' ? 'anual' : freq.toLowerCase();
  const days = (parts.BYDAY ?? '').split(',').map((d) => RRULE_DAYS[d.replace(/^[-+]?\d+/, '')]).filter(Boolean);
  const dayText = days.length ? `, ${days.length > 1 ? `${days.slice(0, -1).join(', ')} y ${days[days.length - 1]}` : days[0]}` : '';
  return `${base}${dayText}`;
}

/** Un chequeo de existencia de una fuente del perfil (evento, etiqueta, space), hecho por el backend contra Google. */
export interface ProfileSourceCheck {
  kind: 'meeting' | 'gmail' | 'chat' | 'slack';
  id: string;
  ok: boolean;
  /** Qué se encontró ("Weekly RTs — semanal, miércoles") o por qué falló. */
  detail: string;
}

export interface ProfilePrBodyArgs {
  profile: BrainProfileConfigInput;
  profileName: string;
  clientName: string;
  submittedBy: string;
  /** Por dónde entró la solicitud: vacío = el formulario de la plataforma; "mcp /add-client (claude.ai)" = el MCP. */
  source?: string;
  /** Canales que el PR suma al JSON del cliente porque el perfil los usa y no estaban. */
  channelsAddedToClient: Array<{ channelId: string; channelName: string }>;
  channelNames: Record<string, string>;
  checks: ProfileSourceCheck[];
  /** El mismo PR onboardea el cliente en General (cliente nuevo). */
  newClient?: boolean;
  /** Cliente nuevo sin nombres de BigQuery: CI lo frena hasta cargarlos. */
  clientWithoutBq?: boolean;
  jsonPath: string;
}

const list = (xs: readonly string[] | undefined, map: (x: string) => string = (x) => x): string => (xs?.length ? xs.map(map).join(', ') : '—');

/** La descripción del PR del perfil: qué entra, qué hay que hacer antes de mergear y qué pasa después. */
export function renderProfilePrBody(a: ProfilePrBodyArgs): string {
  const p = a.profile;
  const ch = (id: string) => (a.channelNames[id] ? `\`#${a.channelNames[id]}\` (${id})` : `\`${id}\``);
  const lines: string[] = [
    `## 🧠 Brain · perfil PML: ${a.profileName}`,
    '',
    `Generado desde ${a.source ? `el MCP (${a.source})` : 'el formulario de la plataforma'} por **${a.submittedBy}**. Con el merge el perfil queda activo y \`/pml ${p.clientId} ${p.id.slice(p.clientId.length + 1)}\` empieza a responder.`,
    '',
    '### Perfil',
    `- **Cliente:** ${a.clientName} (\`${p.clientId}\`)${a.newClient ? ' — **cliente nuevo**: este PR también lo onboardea en General' : ''}`,
    `- **BU:** ${p.bu.name}${p.bu.emoji ? ` ${p.bu.emoji}` : ''} → \`${p.id}\``,
    `- **Owner:** ${p.owner}`,
    `- **Manager:** ${p.manager.name}${p.manager.email ? ` (${p.manager.email})` : ''} · alertas en ${p.manager.alertsChannel ? ch(p.manager.alertsChannel) : '_sin canal (van por DM al owner)_'}`,
    `- **Canales propios:** ${list(p.slack?.channelIds, ch)}`,
    `- **Canales compartidos:** ${list(p.slack?.sharedChannelIds, ch)}${p.slack?.buFilter?.length ? ` · filtro de BU: ${p.slack.buFilter.map((t) => `"${t}"`).join(', ')}` : ''}`,
    `- **Reuniones (Calendar):** ${p.meetings?.length ? p.meetings.map((m) => `${m.label ?? m.recurringEventId}${m.calendarOf ? ` (calendario de ${m.calendarOf})` : ''}`).join('; ') : '—'}`,
    `- **Gmail:** ${p.gmail ? `etiqueta \`${p.gmail.label}\` en ${list(p.gmail.mailboxes)}` : '—'}`,
    `- **Google Chat:** ${p.googleChat ? `${list(p.googleChat.spaces)}${p.googleChat.reader ? ` (lee ${p.googleChat.reader})` : ''}` : '—'}`,
    `- **Status:** ${p.status?.enabled ? `activo, ${list(p.status.days)} → ${p.status.outputChannel ? ch(p.status.outputChannel) : '_sin canal_'}` : 'apagado'}${p.analyticsKey ? ` · analytics key \`${p.analyticsKey}\`` : ''}`,
    `- **Archivo:** \`${a.jsonPath}\` (+ índices regenerados)`,
  ];
  if (a.channelsAddedToClient.length) {
    lines.push('', '### Canales que se suman al cliente', `El sync de Slack indexa por cliente y el perfil solo filtra: estos canales no estaban en \`shared/brain-clients/${p.clientId}.json\` y el PR los agrega.`);
    for (const c of a.channelsAddedToClient) lines.push(`- \`#${c.channelName}\` (${c.channelId})`);
  }
  if (a.clientWithoutBq) {
    lines.push('', '### ⚠️ Sin BigQuery', `El cliente nuevo no tiene nombres de Capacity_Final cargados (no se encontraron por su nombre). El chequeo \`test:brain-clients\` de CI falla hasta cargarlos en \`shared/brain-clients/${p.clientId}.json\` (el Brain no puede contestar revenue ni targets sin eso).`);
  }
  if (a.checks.length) {
    lines.push('', '### Chequeo de fuentes al proponer');
    for (const c of a.checks) lines.push(`- ${c.ok ? '✅' : '⚠️'} ${c.kind === 'meeting' ? 'Reunión' : c.kind === 'gmail' ? 'Gmail' : c.kind === 'chat' ? 'Chat' : 'Slack'} ${c.kind === 'slack' ? ch(c.id) : `\`${c.id}\``}: ${c.detail}`);
    if (a.checks.some((c) => !c.ok)) lines.push('', '_Lo marcado con ⚠️ no frena el merge, pero esa fuente va a salir como "rota" en la cobertura hasta que se arregle._');
  }
  lines.push(
    '',
    '### Checklist antes de mergear',
    '- [ ] El owner confirma que la whitelist es la de esta BU (nada de otras soluciones del cliente)',
    ...(p.manager.alertsChannel ? [`- [ ] El bot de IT está en ${ch(p.manager.alertsChannel)} (alertas de cobertura)`] : []),
    ...(p.status?.outputChannel ? [`- [ ] El bot de IT está en ${ch(p.status.outputChannel)} (salida del status)`] : []),
    ...(p.gmail ? [`- [ ] Cada casilla de Gmail tiene la etiqueta \`${p.gmail.label}\` creada y sabe que solo entran los hilos etiquetados`] : []),
    ...(p.googleChat ? [`- [ ] ${p.googleChat.reader ?? p.owner} es miembro de cada space`] : []),
    ...(p.meetings?.length ? ['- [ ] Las notas de Gemini de cada reunión son legibles por quien organiza (el Brain las lee con su delegación)'] : []),
    '',
    'Al mergear, el deploy corre los syncs de Gmail, Chat y Calendar para este cliente; la cobertura (11:30 AR) avisa al canal del manager lo que falte.',
  );
  return lines.join('\n');
}
